import { SdkContext } from '../internal'
import type { AppInstallation, AppTestResult, CatalogApp, DeliveryStatus, Paged, WebhookDelivery } from '@/types/webhooks'
import { developersRequest, pagedOf } from './webhooks'

/**
 * F-101 1c: the ready-made integrations on the developers-api (owner and
 * admin). Handlers (Discord, Slack, Telegram, RD Station, HubSpot) are turned
 * on and edited with the same PUT; shortcuts (Zapier, Make, n8n) are webhooks.
 */
export class AppsResource {
  constructor(private ctx: SdkContext) {}

  private base(slug: string) {
    return `/apps/${encodeURIComponent(slug)}/installation`
  }

  /** Every app, with its fields, events and the community's installation (or null). */
  async list(): Promise<CatalogApp[]> {
    return (await developersRequest<{ apps: CatalogApp[] }>(this.ctx, 'GET', '/apps')).data.apps || []
  }

  async installation(slug: string): Promise<AppInstallation> {
    return (await developersRequest<{ installation: AppInstallation }>(this.ctx, 'GET', this.base(slug))).data.installation
  }

  /** Turns on or saves. A secret left empty keeps the saved value; turns a disabled installation back on. */
  async save(slug: string, body: { config: Record<string, string>; events?: string[] }): Promise<AppInstallation> {
    return (await developersRequest<{ installation: AppInstallation }>(this.ctx, 'PUT', this.base(slug), { body })).data.installation
  }

  async disable(slug: string): Promise<AppInstallation> {
    return (await developersRequest<{ installation: AppInstallation }>(this.ctx, 'DELETE', this.base(slug))).data.installation
  }

  /** Removes: deletes the config and the copies kept in the deliveries; turning it on again asks for the fields. */
  async remove(slug: string): Promise<null> {
    await developersRequest<{ installation: null }>(this.ctx, 'DELETE', this.base(slug), { query: { remove: 'true' } })
    return null
  }

  /** 200 even when the provider refuses: `ok: false` and the translated `error`. */
  async test(slug: string): Promise<AppTestResult> {
    return (await developersRequest<AppTestResult>(this.ctx, 'POST', `${this.base(slug)}/test`)).data
  }

  /** The groups the bot saw lately; without `botToken`, the saved one. */
  async telegramChats(botToken?: string): Promise<Array<{ id: string; title: string; type: string }>> {
    return (await developersRequest<{ chats: Array<{ id: string; title: string; type: string }> }>(this.ctx, 'POST', '/apps/telegram/chats', {
      body: botToken ? { botToken } : {}
    })).data.chats || []
  }

  async deliveries(slug: string, query: { status?: DeliveryStatus; eventType?: string; page?: number; limit?: number } = {}): Promise<Paged<WebhookDelivery>> {
    return pagedOf(await developersRequest<Record<string, WebhookDelivery[]>>(this.ctx, 'GET', `${this.base(slug)}/deliveries`, { query }), 'deliveries')
  }

  async delivery(slug: string, deliveryId: string): Promise<WebhookDelivery> {
    return (await developersRequest<{ delivery: WebhookDelivery }>(this.ctx, 'GET', `${this.base(slug)}/deliveries/${deliveryId}`)).data.delivery
  }

  async resend(slug: string, deliveryId: string): Promise<WebhookDelivery> {
    return (await developersRequest<{ delivery: WebhookDelivery }>(this.ctx, 'POST', `${this.base(slug)}/deliveries/${deliveryId}/resend`)).data.delivery
  }
}
